import { useQuery } from "@tanstack/react-query";
import type {
  PartnerAppointmentType,
  PartnerLanding,
  ServiceRegistrationForm,
  ServiceRegistrationResult,
  FormQuestion,
} from "@cabocheck/shared";
import {
  buildQuestionnaireSchema,
  processFormDataToAnswers,
  QuestionRenderer,
  toSafeField,
  VaccinationEducationFields,
} from "@cabocheck/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useRef, useState } from "react";
import { Controller, useForm, type FieldValues, type Resolver } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import { PatientQuestionInput } from "~/features/client/PatientQuestionInput";
import { QRCodeDisplay } from "~/features/core/QRCodeDisplay";
import { checkIfQuestionShouldBeHidden, getQuestionDictionary } from "~/features/client/patientQuestions";
import { CalendarLayout } from "./CalendarLayout";

export function PublicRegistration({
  partnerLanding,
  appointmentType,
  onBack,
}: {
  partnerLanding: PartnerLanding;
  appointmentType: PartnerAppointmentType;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const needsProfile = !appointmentType.product_guid && !appointmentType.vaccinations?.length;
  const [profileGuid, setProfileGuid] = useState("");
  const params = new URLSearchParams({
    partner_guid: partnerLanding.partner_guid,
    partner_appointment_type_guid: appointmentType.partner_appointment_type_guid,
  });
  if (profileGuid) params.set("medical_test_profile_guid", profileGuid);
  const { data, isPending, error } = useQuery({
    queryKey: [
      "registration_form",
      partnerLanding.partner_guid,
      appointmentType.partner_appointment_type_guid,
      profileGuid,
    ],
    enabled: !needsProfile || Boolean(profileGuid),
    queryFn: async () => {
      const res = await fetch(`/api/v1/partner/service_registrations?${params}`);
      if (!res.ok) throw new Error("service_unavailable");
      return (await res.json()) as ServiceRegistrationForm;
    },
  });
  return (
    <CalendarLayout
      title={t("Anmeldung ohne Termin")}
      logo={partnerLanding.logo_url}
      partnerName={partnerLanding.partner_name}
      infos={[]}
      onBack={onBack}
      description={t("Füllen Sie Ihre Angaben vor Ihrem Besuch aus. Es wird kein Termin gebucht.")}
    >
      <div className="space-y-6 p-4 xl:p-8">
        {needsProfile ? (
          <label className="grid gap-2">
            {t("Testprofil")}
            <select
              className="rounded-md border p-3"
              value={profileGuid}
              onChange={(event) => setProfileGuid(event.target.value)}
            >
              <option value="">{t("Bitte auswählen")}</option>
              {(partnerLanding.medical_test_profiles ?? [])
                .filter((p) => p.enabled && p.available)
                .map((p) => (
                  <option key={p.medical_test_profile_guid} value={p.medical_test_profile_guid}>
                    {p.name}
                  </option>
                ))}
            </select>
          </label>
        ) : null}
        {error ? <p role="alert">{t("Diese Leistung ist derzeit nicht verfügbar.")}</p> : null}
        {isPending && (!needsProfile || profileGuid) ? <p>{t("Wird geladen...")}</p> : null}
        {data ? (
          <RegistrationForm
            key={`${appointmentType.partner_appointment_type_guid}:${profileGuid}`}
            data={data}
            params={params.toString()}
          />
        ) : null}
      </div>
    </CalendarLayout>
  );
}

function RegistrationForm({ data, params }: { data: ServiceRegistrationForm; params: string }) {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const requestKey = useRef<string>("");
  const [result, setResult] = useState<ServiceRegistrationResult>();
  const [error, setError] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [education, setEducation] = useState<Record<string, { confirmed: boolean; signature: string }>>({});
  const patientQuestions = useMemo(() => {
    const byId = new Map<string, FormQuestion>();
    for (const question of data.services.flatMap((service) => service.patient_details_form?.questions ?? [])) {
      byId.set(question.id, { ...question, required: question.required || Boolean(byId.get(question.id)?.required) });
    }
    return Array.from(byId.values());
  }, [data]);
  const questions = useMemo(
    () =>
      Array.from(
        new Map(
          data.services.flatMap((s) => s.questions).map((q) => [q.product_question_group_question_guid, q]),
        ).values(),
      ),
    [data],
  );
  const educationServices = data.services.filter((service) => service.vaccination_guid && service.education_text);
  const steps = getRegistrationSteps(questions.length > 0, educationServices.length > 0);
  const step = steps[stepIndex]!;
  const personalSchema = z
    .object({
      ...Object.fromEntries(patientQuestions.map((q) => [`patient_${q.id}`, z.string().optional()])),
      privacy: z.literal(true),
    })
    .superRefine((values, ctx) => {
      const patientValues = values as Record<string, unknown>;
      const dictionary = getQuestionDictionary(
        patientQuestions.map((q) => ({
          ...q,
          response: String(patientValues[`patient_${q.id}`] ?? ""),
          required: q.id === "email" || q.id === "dob" || q.required,
        })),
      );
      for (const question of patientQuestions) {
        const input = dictionary[question.id];
        if (!input || !checkIfQuestionShouldBeHidden(dictionary, question.id)) continue;
        const value = String(patientValues[`patient_${question.id}`] ?? "");
        const required =
          input.required && !(question.id === "insurance_number" && dictionary.insurance_type?.value === "PKV");
        if (
          (required && !value.trim()) ||
          ((question.id === "dob" || question.id === "pin") && value && !/^\d{2}\.\d{2}\.\d{4}$/.test(value))
        ) {
          ctx.addIssue({ code: "custom", path: [`patient_${question.id}`], message: t("Bitte ausfüllen.") });
        }
      }
    });
  const questionnaireSchema = buildQuestionnaireSchema(questions);
  const schema = step === "personal" ? personalSchema : step === "questions" ? questionnaireSchema : z.object({});
  const form = useForm<FieldValues>({
    resolver: zodResolver(schema as never) as Resolver<FieldValues>,
    defaultValues: Object.fromEntries(patientQuestions.map((q) => [`patient_${q.id}`, q.response ?? ""])),
  });
  const values = form.watch();
  const dictionary = getQuestionDictionary(
    patientQuestions.map((q) => ({
      ...q,
      response: values[`patient_${q.id}`] ?? "",
      required: q.id === "email" || q.id === "dob" || q.required,
    })) as FormQuestion[],
  );
  const educationComplete = educationServices.every((service) => {
    const entry = education[service.vaccination_guid!];
    return entry?.confirmed && entry.signature;
  });
  async function next() {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
      return;
    }
    await submit();
  }
  async function submit() {
    setError("");
    const urlParams = Object.fromEntries(new URLSearchParams(params));
    const submittedValues = form.getValues();
    if (!personalSchema.safeParse(submittedValues).success) {
      setStepIndex(0);
      return;
    }
    if (!questionnaireSchema.safeParse(submittedValues).success) {
      setStepIndex(steps.indexOf("questions"));
      return;
    }
    if (!educationComplete) return;
    const responses = patientQuestions.map((q) => ({
      id: q.id,
      response: String(submittedValues[`patient_${q.id}`] ?? ""),
    }));
    const answerData = new FormData();
    for (const question of questions) {
      const guid = question.product_question_group_question_guid;
      answerData.append(`type-${guid}`, question.question_type);
      answerData.append(`answer-${guid}`, String(submittedValues[toSafeField(guid)] ?? ""));
    }
    try {
      if (!requestKey.current) requestKey.current = createRegistrationRequestKey();
      const res = await fetch("/api/v1/partner/service_registrations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...urlParams,
          request_key: requestKey.current,
          email: submittedValues.patient_email,
          patient_responses: responses,
          answers: processFormDataToAnswers(answerData),
          education: educationServices.map((service) => ({
            vaccination_guid: service.vaccination_guid,
            education_text: service.education_text,
            education_confirmed: education[service.vaccination_guid!]!.confirmed,
            education_signature: education[service.vaccination_guid!]!.signature.split(",")[1] ?? "",
            education_signature_mime_type: "image/png",
          })),
        }),
      });
      if (res.status === 429 || res.status === 503) {
        setError(
          res.status === 429
            ? t("Zu viele Anfragen. Bitte versuchen Sie es später erneut.")
            : t("Die Anmeldung ist vorübergehend nicht verfügbar. Bitte versuchen Sie es später erneut."),
        );
        return;
      }
      if (!res.ok) throw new Error("registration_failed");
      setResult((await res.json()) as ServiceRegistrationResult);
    } catch {
      setError(t("Bitte prüfen Sie Ihre Angaben und versuchen Sie es erneut."));
    }
  }
  if (result)
    return (
      <div className="space-y-4 text-center">
        <h2 className="text-xl font-bold">{t("Ihre CaboCheck-IDs")}</h2>
        <p>{t("Bitte zeigen Sie die passende CaboCheck-ID in der Apotheke vor.")}</p>
        {result.registrations.map((r) => (
          <div key={r.code} className="space-y-2 rounded-lg border p-4">
            <p>{r.service_name}</p>
            <p className="text-2xl font-bold tracking-widest">{r.code}</p>
            <div className="flex justify-center">
              <QRCodeDisplay url={`https://cabocheck.com/qr/${r.code}`} size={160} />
            </div>
          </div>
        ))}
        <p>
          {result.email_sent
            ? t("Ihre CaboCheck-IDs wurden per E-Mail versendet.")
            : t("Die E-Mail konnte nicht versendet werden. Bitte speichern Sie Ihre CaboCheck-IDs.")}
        </p>
      </div>
    );
  return (
    <form ref={formRef} onSubmit={form.handleSubmit(next)} className="space-y-6">
      <ol className="flex flex-wrap gap-4 text-sm" aria-label={t("Anmeldung")}>
        {steps.map((item, index) => (
          <li
            key={item}
            aria-current={index === stepIndex ? "step" : undefined}
            className={index === stepIndex ? "font-semibold text-blue-700" : "text-gray-500"}
          >
            {index + 1}.{" "}
            {item === "personal"
              ? t("Persönliche Angaben")
              : item === "questions"
                ? t("Zusätzliche Fragen")
                : t("Aufklärung")}
          </li>
        ))}
      </ol>
      <h2 className="text-xl font-semibold">
        {step === "personal"
          ? t("Persönliche Angaben")
          : step === "questions"
            ? t("Zusätzliche Fragen")
            : t("Aufklärung")}
      </h2>
      {step === "personal" ? (
        <>
          {patientQuestions
            .filter((q) => checkIfQuestionShouldBeHidden(dictionary, q.id))
            .map((q) => (
              <Controller
                key={q.id}
                name={`patient_${q.id}`}
                control={form.control}
                render={({ field }) => (
                  <label className="grid gap-2">
                    {dictionary[q.id]!.label}
                    <PatientQuestionInput
                      input={{
                        ...dictionary[q.id]!,
                        value: String(field.value ?? ""),
                        required:
                          dictionary[q.id]!.required &&
                          !(q.id === "insurance_number" && dictionary.insurance_type?.value === "PKV"),
                      }}
                      onChange={field.onChange}
                    />
                    {form.formState.errors[`patient_${q.id}`] ? (
                      <span className="text-red-600">{t("Bitte ausfüllen.")}</span>
                    ) : null}
                  </label>
                )}
              />
            ))}
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 size-5 shrink-0 accent-blue-600"
              required
              {...form.register("privacy")}
            />
            <span>
              {t("Ich akzeptiere die AGB und die Datenschutzerklärung.")}{" "}
              <a href="/agb" target="_blank" rel="noreferrer" className="underline">
                {t("AGB")}
              </a>
              {" · "}
              <a href="/datenschutz" target="_blank" rel="noreferrer" className="underline">
                {t("Datenschutzerklärung")}
              </a>
            </span>
          </label>
        </>
      ) : null}
      {step === "questions"
        ? questions.map((q, index) => (
            <QuestionRenderer
              key={q.product_question_group_question_guid}
              question={q}
              index={index}
              control={form.control}
              errors={form.formState.errors}
              formRef={formRef}
            />
          ))
        : null}
      {step === "education"
        ? educationServices.map((service) => {
            const guid = service.vaccination_guid!;
            const entry = education[guid] ?? { confirmed: false, signature: "" };
            return (
              <section key={guid} className="space-y-4">
                <h3 className="font-semibold">{service.service_name}</h3>
                <VaccinationEducationFields
                  text={service.education_text!}
                  confirmed={entry.confirmed}
                  signature={entry.signature}
                  onConfirmedChange={(confirmed) =>
                    setEducation((prev) => ({ ...prev, [guid]: { ...entry, confirmed } }))
                  }
                  onSignatureChange={(signature) =>
                    setEducation((prev) => ({ ...prev, [guid]: { ...entry, signature } }))
                  }
                />
              </section>
            );
          })
        : null}
      {error ? (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      ) : null}
      <div className="flex gap-3">
        {stepIndex > 0 ? (
          <Button
            type="button"
            variant="outline"
            disabled={form.formState.isSubmitting}
            onClick={() => setStepIndex(stepIndex - 1)}
          >
            {t("Zurück")}
          </Button>
        ) : null}
        <Button type="submit" disabled={form.formState.isSubmitting || (step === "education" && !educationComplete)}>
          {stepIndex === steps.length - 1 ? t("Anmeldung abschließen") : t("Weiter")}
        </Button>
      </div>
    </form>
  );
}

export function getRegistrationSteps(
  hasQuestions: boolean,
  hasEducation: boolean,
): ("personal" | "questions" | "education")[] {
  return ["personal", ...(hasQuestions ? ["questions" as const] : []), ...(hasEducation ? ["education" as const] : [])];
}

export function createRegistrationRequestKey(): string {
  // getRandomValues also works on HTTP origins, unlike randomUUID.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
